import SimpleCheckbox from '../questions/simpleQuestions/SimpleCheckbox';
import SimpleInput from '../questions/simpleQuestions/SimpleInput';
import SimpleRadio from '../questions/simpleQuestions/SimpleRadio';
import SimpleRadioWithInputOption from '../questions/simpleQuestions/SimpleRadioWithInputOption';
import type { QuestionLibraryError } from '../utils/errorHandling';

type QuestionMapperProps = {
  type?: string;
  question?: Record<string, any>;
  field?: any;
  error?: QuestionLibraryError;
  disabled?: boolean;
};

/**
 * @typedef {object} QuestionMapperProps
 * @property {string} type - question type
 * @property {import('../types/question')} question - question object
 * @property {object} field - react-hook-form field object
 * @property {string} error - react-hook-form error object
 *
 * @param {QuestionMapperProps} props
 * @returns {JSX.Element}
 * @description QuestionMapper component that renders a simple question based on the question type.
 */

const simpleInputTypes = new Set([
  'simpleDate',
  'simpleCurrency',
  'simpleSelect',
  'simpleText',
  'simpleNumber',
  'simpleTextArea',
  'simpleSwitch',
  'simplePercentage',
]);

export default function QuestionMapper({
  type,
  question,
  field,
  error,
  disabled,
}: Readonly<QuestionMapperProps>) {
  if (simpleInputTypes.has(type ?? '')) {
    // Remove 'simple' prefix and lowercase the first letter for inputType
    const inputType = type?.replace('simple', '').toLowerCase();
    return (
      <SimpleInput
        inputType={inputType}
        field={field}
        error={error}
        disabled={disabled}
        {...question}
      />
    );
  }

  if (type === 'simpleRadio') {
    return <SimpleRadio field={field} error={error} {...question} />;
  }

  if (type === 'simpleRadioWithInputOption') {
    return (
      <SimpleRadioWithInputOption field={field} error={error} {...question} />
    );
  }

  if (type === 'simpleCheckbox') {
    return <SimpleCheckbox field={field} error={error} question={question} />;
  }

  return (
    <p>
      No question of type <strong>{question?.type}</strong>
    </p>
  );
}
